import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from 'v-select2-multiple-component';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";
import ValidationService from "@/services/ValidationService";
import store from '@/store';

const maxSize5MB = (value) => {
  if (!value) return true;
  return value.size <= 5 * 1024 * 1024;
};

export default {
  name: "ClassImport",
  components: {
    MainLayout,
    Select2Multiple,
    flatPickr,
    Loading,
  },

  mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

  data() {
    return {
      isLoading: false,
      disableConfirmBtn: false,
      periodArr: [],
      template_academic_year_id: null,
      academic_year_id: null,
      selectedFile: null,
      file_name: '',
      submitted: false,
      templateSubmitted: false,
      invalidFileType: false,
      importStep: 'upload',
      previewRows: [],
      previewTotal: 0,
      selectedAcademicYearName: '',
      disableAcademicYear: false,
      disableImportBtn: false,
      gradeOptions: [],
      teacherOptions: [],
    };
  },

  validations: {
    academic_year_id: {
      required
    },
    selectedFile: {
      required,
      maxSize5MB
    },
  },

  computed: {
    isPreviewStep() {
      return this.importStep === 'preview';
    },

    disabledTemplateBtn() {
      return this.isPreviewStep;
    },
  },

  methods: {
    getAcademicYearList() {
      this.isLoading = true;
      axios.get("period/periodList")
      .then(res => {
        this.periodArr = res.data.data || [];
        this.isLoading = false;
      }).catch((error) => {
        this.handleError(error);
        this.isLoading = false;
      });
    },

    getGradeList(yearId) {
      if (!yearId) {
        this.gradeOptions = [];
        return;
      }

      axios.get("campusGrade/list-by-acdYear", {
        params: {
          campus_id: store.state.user.campus_id,
          acd_year_id: yearId,
          get_grade: true,
        }
      }).then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        this.gradeOptions = rows.map((grade) => ({
          id: grade.campus_grade_id,
          text: grade.grade_name,
        }));
      }).catch((error) => {
        this.gradeOptions = [];
        this.handleError(error);
      });
    },

    onGradeChange(rowIndex, selectedId) {
      const row = this.previewRows[rowIndex];
      if (!row) return;
      const option = this.gradeOptions.find(
        (g) => String(g.id) === String(selectedId)
      );
      if (option) {
        this.$set(row, "grade_name", option.text);
      } else if (selectedId === null || selectedId === undefined || selectedId === "") {
        this.$set(row, "grade_name", "");
      }
      this.onPreviewFieldInput(rowIndex, "grade_name");
    },

    onHomeroomTeacherChange(rowIndex, selectedId) {
      const row = this.previewRows[rowIndex];
      if (!row) return;
      const option = this.teacherOptions.find(
        (t) => String(t.id) === String(selectedId)
      );
      if (option) {
        this.$set(row, "homeroom_teacher_name", option.text);
      } else if (selectedId === null || selectedId === undefined || selectedId === "") {
        this.$set(row, "homeroom_teacher_name", "");
      }
      this.onPreviewFieldInput(rowIndex, "homeroom_teacher_name");
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.invalidFileType = false;

      if (!file) {
        this.selectedFile = null;
        this.file_name = '';
        return;
      }

      const allowedTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      const extension = file.name.split('.').pop().toLowerCase();
      const validExtensions = ['xls', 'xlsx'];

      if (!validExtensions.includes(extension) && !allowedTypes.includes(file.type)) {
        this.invalidFileType = true;
        this.selectedFile = null;
        this.file_name = '';
        this.$refs.fileUploader.value = null;
        return;
      }
      this.invalidFileType = false;
      this.selectedFile = file;
      this.file_name = file.name;
    },

    chooseFile() {
      if (this.isPreviewStep) {
        return;
      }
      this.invalidFileType = false;
      this.$refs.fileUploader.value = null;
      this.selectedFile = null;
      this.file_name = '';
      this.$refs.fileUploader.click();
    },

    resetForm() {
      this.template_academic_year_id = null;
      this.academic_year_id = null;
      this.selectedFile = null;
      this.file_name = '';
      this.submitted = false;
      this.templateSubmitted = false;
      this.importStep = 'upload';
      this.previewRows = [];
      this.previewTotal = 0;
      this.clearImportConfirmErrors();
      this.selectedAcademicYearName = '';
      this.disableAcademicYear = false;
      this.invalidFileType = false;
      this.disableImportBtn = false;
      this.disableConfirmBtn = false;
      this.$v.$reset();
      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
      this.getAcademicYearList();
    },

    downloadTemplate() {
      this.templateSubmitted = true;

      if (this.disabledTemplateBtn) {
        return;
      }

      const params = {};
      if (this.template_academic_year_id) {
        params.academic_year_id = this.template_academic_year_id;
      }

      this.isLoading = true;
      axios.get("class/excelTemplateDownload", {
        params,
        responseType: "blob",
      })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const link = document.createElement("a");

          link.href = window.URL.createObjectURL(blob);
          link.download = this.getTemplateFileName();
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getTemplateFileName() {
      const selected = this.periodArr.find(
        (period) => String(period.id) === String(this.template_academic_year_id)
      );
      const periodName = (selected?.text || "all").replace(/\s+/g, "_");
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      return `${periodName}_classes_${year}_${month}_${day}.xlsx`;
    },

    confirmImport() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid || !this.selectedFile) {
        return;
      }
      this.validateImport();
    },

    validateImport() {
      this.isLoading = true;
      this.disableImportBtn = true;

      const formData = new FormData();
      formData.append('academic_year_id', this.academic_year_id);
      formData.append('file', this.selectedFile);

      axios.post('/class/excelImportValidate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      }).then(response => {
        this.previewRows = this.clonePreviewRows(response.data.preview || []);
        this.previewTotal = response.data.total || this.previewRows.length;
        this.teacherOptions = response.data.teacher_options || [];
        this.clearImportConfirmErrors();
        this.importStep = 'preview';

        if (this.previewRows.length > 0) {
          const yearName = this.periodArr.find(y => y.id == this.academic_year_id);
          this.selectedAcademicYearName = yearName ? yearName.text : '';
        }

        this.getGradeList(this.academic_year_id);
        this.disableAcademicYear = true;
      }).catch(error => {
        this.handleImportValidationError(error);
        this.disableImportBtn = false;
      }).finally(() => {
        this.isLoading = false;
      });
    },

    confirmImportSubmit() {
      this.clearImportConfirmErrors();

      if (!this.previewRows.length) {
        this.$notification.error("No preview data to import.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      const fieldErrors = this.validatePreviewBeforeConfirm();
      if (Object.keys(fieldErrors).length) {
        this.setConfirmFieldErrors(fieldErrors);
        return;
      }

      this.isLoading = true;
      this.disableConfirmBtn = true;

      axios.post('/class/excelImportConfirm', {
        academic_year_id: this.academic_year_id,
        preview: this.buildConfirmPreviewPayload(),
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      }).then(response => {
        this.$notification.success(response.data.message || 'Classes imported successfully.', {
          timer: 3,
          position: "bottomCenter"
        });
        this.$router.push({ name: 'classList' });
      }).catch(error => {
        this.handleConfirmBackendError(error);
      }).finally(() => {
        this.disableConfirmBtn = false;
        this.isLoading = false;
      });
    },

    clonePreviewRows(rows) {
      return (rows || []).map((row) => ({
        grade_name: String(row.grade_name || "").trim(),
        section: String(row.section || "").trim(),
        start_date: row.start_date || "",
        end_date: row.end_date || "",
        remark: row.remark ?? "",
        homeroom_teacher_name: String(row.homeroom_teacher_name || "").trim(),
        assistant_teacher_names: row.assistant_teacher_names ?? "",
        campus_grade_id: row.campus_grade_id,
        homeroom_teacher_id: row.homeroom_teacher_id,
        assistant_teacher_ids: row.assistant_teacher_ids,
      }));
    },

    buildConfirmPreviewPayload() {
      return this.previewRows.map((row) => ({
        grade_name: String(row.grade_name || "").trim(),
        section: String(row.section || "").trim(),
        start_date: row.start_date || null,
        end_date: row.end_date || null,
        remark: String(row.remark || "").trim() === "" ? null : String(row.remark).trim(),
        homeroom_teacher_name: String(row.homeroom_teacher_name || "").trim(),
        assistant_teacher_names: String(row.assistant_teacher_names || "").trim() === ""
          ? null
          : String(row.assistant_teacher_names).trim(),
        campus_grade_id: row.campus_grade_id,
        homeroom_teacher_id: row.homeroom_teacher_id,
        assistant_teacher_ids: row.assistant_teacher_ids,
      }));
    },

    validatePreviewBeforeConfirm() {
      const errors = {};
      const seenKeys = {};

      this.previewRows.forEach((row, index) => {
        const rowErrors = {};
        const gradeName = String(row.grade_name || "").trim();
        const section = String(row.section || "").trim();
        const remark = String(row.remark || "").trim();
        const homeroomTeacherName = String(row.homeroom_teacher_name || "").trim();

        if (!gradeName) {
          rowErrors.grade_name = "Grade Name is required.";
        } else if (gradeName.length > 255) {
          rowErrors.grade_name = "Max 255 characters.";
        }

        if (!section) {
          rowErrors.section = "Section is required.";
        } else if (section.length > 20) {
          rowErrors.section = "Max 20 characters.";
        } else if (gradeName) {
          const dupKey = `${gradeName.toLowerCase()}||${section.toLowerCase()}`;
          if (seenKeys[dupKey]) {
            rowErrors.section = "Duplicate Grade + Section combination.";
          } else {
            seenKeys[dupKey] = true;
          }
        }

        if (remark.length > 1000) {
          rowErrors.remark = "Max 1000 characters.";
        }

        if (!homeroomTeacherName) {
          rowErrors.homeroom_teacher_name = "Homeroom Teacher is required.";
        }

        if (Object.keys(rowErrors).length) {
          errors[index] = rowErrors;
        }
      });

      return errors;
    },

    handleError(error) {
      if (error.response?.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
        return;
      }

      const message = error.response?.data?.message || "Something went wrong.";
      this.$notification.error(message, {
        timer: 3,
        position: "bottomCenter",
      });
    },

    handleImportValidationError(error) {
      if (error.response?.status === 422) {
        const data = error.response.data || {};
        const messages = data.errors?.length
          ? data.errors
          : [data.message || "Import validation failed."];

        window.alert(messages.join("\n"));
        return;
      }

      this.handleError(error);
    },

    handleConfirmBackendError(error) {
      if (this.applyConfirmBackendErrors(error, this.getImportConfirmErrorOptions())) {
        return;
      }
      this.handleError(error);
    },

    getImportConfirmErrorOptions() {
      return {};
    },
  },

  mounted() {
    this.getAcademicYearList();
  },
};
